import type { AppState, FunnelStep, SessionStatus } from '../types';
import { ALUNAR_FUNNEL_VERSION } from './funnel-version';

export const STORAGE = { visitor: 'arcana_visitor_id', resume: 'arcana_resume_token', session: 'arcana_latest_session', readingCredentials: 'arcana_reading_credentials_v1', consent: 'arcana_consent_v1', legacyConsent: 'arcana_consent' } as const;

export function getVisitorId(): string {
  const existing = localStorage.getItem(STORAGE.visitor);
  if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(STORAGE.visitor, id);
  return id;
}

export function initialState(): AppState {
  const params = new URLSearchParams(location.search);
  return {
    visitor_id: getVisitorId(), first_name: '', birth_date: '', focus_selections: [], step: 'landing', status: 'started', funnel_version: ALUNAR_FUNNEL_VERSION,
    prefilled: false, returning_visitor: Boolean(localStorage.getItem(STORAGE.resume)),
    source: cleanAttribution(params.get('utm_source')), medium: cleanAttribution(params.get('utm_medium')), campaign: cleanAttribution(params.get('utm_campaign')),
  };
}

export function hydratedBirthDetails(input:{birth_time_status?:AppState['birth_time_status']|null;birth_hour?:number|null;birth_minute?:number|null;birth_meridiem?:AppState['birth_meridiem']|null;birthplace?:string|null}):Pick<AppState,'birth_time_status'|'birth_hour'|'birth_minute'|'birth_meridiem'|'birthplace'>{
  const birthTimeStatus=input.birth_time_status??undefined;
  return{
    birth_time_status:birthTimeStatus,
    birth_hour:birthTimeStatus==='known'?input.birth_hour??undefined:undefined,
    birth_minute:birthTimeStatus==='known'?input.birth_minute??undefined:undefined,
    birth_meridiem:birthTimeStatus==='known'?input.birth_meridiem??undefined:undefined,
    birthplace:input.birthplace??undefined,
  };
}

export function cleanAttribution(value: string | null): string | undefined {
  if (typeof value !== 'string') return undefined;
  const cleaned = value.trim();
  if (!cleaned || cleaned.length > 120 || /\s|[\u0000-\u001f\u007f<>?&#]/.test(cleaned)) return undefined;
  if (!/^[a-z0-9][a-z0-9._:/+-]*$/i.test(cleaned) || /@|%40|mailto:|(?:^|\D)\d{7,}(?:\D|$)/i.test(cleaned)) return undefined;
  return cleaned;
}

const ALLOWED: Record<SessionStatus, SessionStatus[]> = {
  started: ['details_completed', 'abandoned'], details_completed: ['focus_completed', 'abandoned'],
  focus_completed: ['generating_free', 'abandoned'], generating_free: ['free_ready', 'generation_failed'],
  free_ready: ['checkout_started', 'abandoned'], checkout_started: ['purchased', 'free_ready'],
  purchased: ['generating_paid'], generating_paid: ['paid_ready', 'generation_failed'], paid_ready: [],
  generation_failed: ['generating_free', 'generating_paid', 'abandoned'], abandoned: ['started'],
};

export function transition(state: AppState, status: SessionStatus, step?: FunnelStep): AppState {
  if (!ALLOWED[state.status].includes(status)) throw new Error(`Invalid transition: ${state.status} -> ${status}`);
  return { ...state, status, step: step ?? state.step };
}
